import { HdJustNowTransitColorMode } from '../mandala/just-now-transit/hd-just-now-transit';
import { HdBodygraphColorState, HdBodygraphGateState } from './hd-bodygraph.models';

export type HdGateTransitVisual =
  | { readonly kind: 'inactive' }
  | { readonly kind: 'base'; readonly baseColorState: HdBodygraphColorState }
  | { readonly kind: 'transit' }
  | { readonly kind: 'split-base-transit' }
  | { readonly kind: 'hatched-personality' }
  | { readonly kind: 'hatched-design' }
  | { readonly kind: 'hatched-both' }
  | { readonly kind: 'hatched-highlight' };

export function resolveHdGateTransitVisual(
  baseGateState: HdBodygraphGateState,
  hasTransit: boolean,
  colorMode: HdJustNowTransitColorMode,
): HdGateTransitVisual {
  return resolveHdTransitVisual(baseGateState.isActive, baseGateState.colorState, hasTransit, colorMode);
}

/**
 * BELANGRIJK: de "bronruimte" van een overlay. Elke poort wordt vertaald naar
 * P/D-vorm volgens wélke kant hem activeert: basis (chart 1) speelt de rol van
 * personality (de `b`-sublaag, zwart), overlay (chart 2 / transit) die van
 * design (de `r`-sublaag, rood), beide samen die van 'both' — dezelfde
 * verdeling die `split-base-transit` al op de sublagen schildert.
 *
 * Waarom: de bruglagen en corner-caps van het integratiegebied (10/20/34/57)
 * krijgen een afgeleide kleur uit de kanalen die erdoor lopen. Werd die
 * afleiding op de rauwe P/D-kleuren gedaan en het stuk daarna in de kleur van
 * zijn eigenaarpoort geschilderd, dan kleurde bv. het brugstuk c5720 (eigendom
 * van 20) in de overlaykleur terwijl het basiskanaal 10-34 erdoor loopt — dat
 * kanaal leek onderbroken (Svetlana's solar return 2026). Dezelfde afleiding in
 * de bronruimte zegt per stuk welke flows (basis, overlay of beide) erdoor
 * lopen; de twee-kleurenmodus rendert zo exact als een gewone chart met andere
 * kleuren. Specs: "integratiebrug in de twee-kleurenmodus" en "... in de
 * driekleurenmodus" in hd-bodygraph-chart.component.spec.ts.
 */
export function toHdOverlaySourceGateState(
  gateState: HdBodygraphGateState,
  baseIsActive: boolean,
  hasTransit: boolean,
): HdBodygraphGateState {
  const colorState: HdBodygraphColorState = baseIsActive && hasTransit
    ? 'both'
    : baseIsActive ? 'personality' : hasTransit ? 'design' : 'inactive';
  return { ...gateState, hasPersonality: baseIsActive, hasDesign: hasTransit, colorState };
}

/**
 * Overlay-weergave van een laag: `sourceState` (bronruimte, zie
 * {@link toHdOverlaySourceGateState}) zegt welke flows erdoor lopen,
 * `colorState` welke P/D-kleur het basisdeel draagt. Twee-kleuren: basis,
 * overlay of gesplitst. Driekleuren: basis houdt zijn P/D-kleur, enkel overlay
 * is effen groen, beide samen is de P/D-kleur gearceerd.
 */
export function resolveHdOverlayLayerVisual(
  sourceState: HdBodygraphColorState,
  colorState: HdBodygraphColorState,
  colorMode: HdJustNowTransitColorMode,
): HdGateTransitVisual {
  const hasBase = sourceState === 'personality' || sourceState === 'both';
  const hasTransit = sourceState === 'design' || sourceState === 'both';
  return resolveHdTransitVisual(hasBase, colorState, hasTransit, colorMode);
}

function resolveHdTransitVisual(
  baseIsActive: boolean,
  baseColorState: HdBodygraphColorState,
  hasTransit: boolean,
  colorMode: HdJustNowTransitColorMode,
): HdGateTransitVisual {
  if (!baseIsActive && !hasTransit) {
    return { kind: 'inactive' };
  }
  if (!baseIsActive) {
    return { kind: 'transit' };
  }
  if (!hasTransit) {
    return { kind: 'base', baseColorState };
  }
  if (colorMode === 'two-color') {
    return { kind: 'split-base-transit' };
  }

  switch (baseColorState) {
    case 'personality':
      return { kind: 'hatched-personality' };
    case 'design':
      return { kind: 'hatched-design' };
    case 'both':
      return { kind: 'hatched-both' };
    case 'highlight':
      return { kind: 'hatched-highlight' };
    case 'inactive':
      return { kind: 'transit' };
  }
}
